import { ProfileRealEstateType, ProfileTangibleAssetType } from '.';
import { CollateralSecurityTypes, FacilityTypes } from './DealDebtSchedule';
import { LoanOptionType } from './DealTypePreference';

export interface TrancheCapacity {
	Tranche: string;
	tier1BankType: Tier1BankTypes;
	standardDeviation: number;
	lowCapacity: number;
	averageCapacity: number;
	highCapacity: number;
	isDefaultValue: boolean;
}

export type Tier1BankTypes = keyof typeof Tier1BankTypeMap;
export const Tier1BankTypeMap = {
	Commercial: 'Commercial Bank',
	NonBank: 'Non-Bank',
	Other: 'Other',
};

export type Tier2BankTypes = keyof typeof Tier2BankTypeMap;
export const Tier2BankTypeMap = {
	International: 'International Bank',
	National: 'National Bank',
	Regional: 'Regional Bank',
	CreditUnion: 'Credit Union',
	CommunityBank: 'Community Bank',
	Sblc: 'SBLC',
	Sbic: 'SBIC',
	Bdc: 'BDC',
	DirectLender: 'Direct Lender',
	VentureLender: 'Venture Lender',
	AssetBased: 'Asset-Based Lender',
	FamilyOffice: 'Family Office',
	PrivateEquity: 'Private Equity',
};

export type Tier3ContactTypes = keyof typeof Tier3ContactTypeMap;
export const Tier3ContactTypeMap = {
	Sba7a: 'SBA 7a',
	Sba504: 'SBA 504',
	SeniorCash: 'Senior - Cash Flow',
	AssetWorkingCapital: 'Asset Based - Working Capital',
	AssetEquipment: 'Asset Based - Tangibles / M&E',
	RealEstateSenior: 'Commercial Real Estate - Senior',
	MiddleMarket: 'Middle Market - Industry Specialist',
	LeveragedFinance: 'Leveraged Finance',
	SyndicatedCapitalMarkets: 'Syndicated Capital Markets',
	JuniorCash: 'Junior / Second Lien',
	Mezzanine: 'Mezzanine',
	StructuredPrivateEquity: 'Structured Equity / Preferred Equity',
	RealEstateJunior: 'Commercial Real Estate - Junior/Mezz',
	EquityMinority: 'Equity Co-Invest / Minority Equity',
	EquityControl: 'Control Equity',
	UnitrancheStretchSenior: 'Unitranche/Stretch Senior',
	VentureCapital: 'Venture Capital (Equity)',
	MiddleMarketSpecialist: 'Middle Market Specialist',
	AssetIntangible: 'Asset Based - Intangibles',
	Usda: 'USDA',
	VentureDebt: 'Venture Debt',
	RecurringRevenue: 'Recurring Revenue',
	BridgeMcaLending: 'Bridge / MCA Lending',
};

export interface TrancheEvaluation {
	id: number;
	guid: string;
	loanOptionType?: LoanOptionType;
	metric?: number;
	isEligible?: boolean;
	structureTranche?: StructureTranches;
	profileRealEstateType?: ProfileRealEstateType;
	tier1BankType?: Tier1BankTypes;
	profileTangibleAssetType?: ProfileTangibleAssetType;
	collateralSecurityType: CollateralSecurityTypes;
	facilityType1?: FacilityTypes;
	facilityType2?: FacilityTypes;
	numberMatches: number;
	numberMatchesFinderFee: number;
	capacities: TrancheCapacity;
	overrideCapacity?: number;
	ebitda: number;
	collateral?: number;
	dscr?: number;
	baseRateType: TypicalCreditSpreadTypes;
	minimumDscr: number;
	multiplier?: number;
	baseRate?: number;
	rate?: number;
	amortization?: number;
	metricCapacity: number;
	negativeEbitdaCapacity: boolean;
	isLimitedByCollateral: boolean;
	requiredEbitda?: number;
	matchEbitdaRequirement: boolean;
	hasValue: boolean;
	termSheetMatches: number;
	showEvaluation: boolean;
}

export type StructureTranches = keyof typeof StructureTrancheMap;
export const StructureTrancheMap = {
	SeniorCashFlow: 'Senior Cash Flow',
	JuniorCashFlow: 'Junior Cash Flow',
	VentureDebt: 'Venture Debt',
	Equity: 'Equity',
	SeniorRealEstate: 'Commercial Real Estate - Senior',
	JuniorRealEstate: 'Commercial Real Estate - Junior/Mezz',
	Sba7aRealEstate: 'SBA 7a - Real Estate',
	Sba7aNonRealEstate: 'SBA 7a - Non-Real Estate',
	Sba504Senior: 'SBA 504 - Senior',
	Sba504Debenture: 'SBA 504 - Debenture',
	UsdaBiRealEstate: 'USDA B&I - Real Estate',
	UsdaBiEquipment: 'USDA B&I - Equipment',
	UsdaBiWorkingCapital: 'USDA B&I - Working Capital',
	AssetBasedWorkingCapitalAccountsReceivable: 'ABL Working Capital - Accounts Receivable',
	AssetBasedWorkingCapitalInventory: 'ABL Working Capital - Inventory',
	AssetBasedEquipmentMachinery: 'ABL Equipment - Machinery & Equipment',
	AssetBasedEquipmentVehicles: 'ABL Equipment - Rolling Stock / Vehicles',
	AssetBasedEquipmentMarine: 'ABL Equipment - Marine Vessels',
	AssetBasedEquipmentHelicopters: 'ABL Equipment - Helicopters',
	AssetBasedEquipmentAircraft: 'ABL Equipment - Fixed Wing Aircraft',
	AssetBasedIntangibleIntellectualProperty: 'ABL Intangibles/Other - Intellectual Property',
	AssetBasedIntangibleRoyalty: 'ABL Intangibles/Other - Royalty/Licensing Agreements',
	PoFinancing: 'Purchase Order Financing',
	Factoring: 'Factoring',
	RecurringRevenue: 'Recurring Revenue',
	InventoryOnly: 'Inventory Only',
	LenderFinance: 'Lender Finance',
	FloorplanFinance: 'Floorplan Finance',
	AssetBasedWorkingCapitalArPlusInventory: 'ABL Working Capital - AR / Inventory',
	BridgeLoan: 'Bridge Loan',
	Unknown: 'Unknown',
};

export type TypicalCreditSpreadTypes = keyof typeof TypicalCreditSpreadTypeMap;
export const TypicalCreditSpreadTypeMap = {
	NotSelected: 'Not Selected',
	Libor: 'LIBOR',
	Prime: 'Prime',
	Fixed: 'Fixed',
	Sofr: 'SOFR',
};
